import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
    AlertTriangle,
    Calendar,
    History,
    Printer,
    Send,
    User,
    Car,
    Wrench,
    X,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { httpClient } from '../../../common/httpclient';
import ChecklistWhatsappDialog from '../../../component/dialog/ChecklistWhatsappDialog';
import { usePermissions } from '../../../hooks/usePermissions';

/**
 * Center modal — DESIGN-SYSTEM.md §5.6 (scrim + 20px box + header
 * ícone-badge/título/descrição + X + corpo rolável + rodapé com ação à
 * direita), mesmo padrão real de `pages/cashregisters/components/CenterModal.js`.
 * Não reaproveita esse componente compartilhado 1:1 porque o rodapé daqui é
 * um único "Fechar" (sem Cancelar/Confirmar) e cada entrada do histórico tem
 * sua própria ação de impressão — construído localmente com os mesmos
 * tokens/paddings para ficar visualmente idêntico.
 *
 * Reaproveitado por `ChecklistReport.js` (`/reports/checklist`, drill-down
 * "Ver histórico" por modelo de checklist) — único consumidor hoje.
 *
 * Cobertura de dados: `GET /checklist/{id}/history` devolve, por
 * preenchimento, só metadados (nº da O.S., cliente, veículo, mecânico, data
 * de conclusão) — não devolve grupos/itens/respostas (OK/NOK/Manutenção)
 * como o protótipo `prototipo-relatorio-checklist.html` mostra por
 * preenchimento. Por isso cada entrada aqui mostra só os metadados reais,
 * sem os grupos de itens com pill de resposta.
 */
function InfoBlock({ icon: Icon, label, value }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box sx={{
                width: 30,
                height: 30,
                borderRadius: '9px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'info.bg',
                color: 'primary.main',
                flexShrink: 0,
            }}>
                <Icon size={14} strokeWidth={2} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '10.5px', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                    {label}
                </Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {value || 'N/A'}
                </Typography>
            </Box>
        </Box>
    );
}

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(date);
    } catch (error) {
        return dateString;
    }
};

export default function ChecklistHistoryModal({ open, onClose, checklistId }) {
    const [loading, setLoading] = useState(true);
    const [historyData, setHistoryData] = useState(null);
    const [error, setError] = useState(null);
    const [printingIds, setPrintingIds] = useState(new Set());
    const { hasPermission } = usePermissions();
    const canSendWhatsapp = hasPermission('serviceorder.send');
    const [whatsappTarget, setWhatsappTarget] = useState(null); // { serviceOrderId, orderNumber }
    const [feedback, setFeedback] = useState(null); // { message, severity }

    useEffect(() => {
        if (open && checklistId) {
            fetchHistory();
        } else {
            // Reset state when modal closes
            setHistoryData(null);
            setError(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, checklistId]);

    const fetchHistory = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await httpClient.get(`/checklist/${checklistId}/history`);

            if (response.status === 200 && response.data) {
                setHistoryData(response.data);
            } else {
                setError('Erro ao carregar histórico do checklist');
            }
        } catch (error) {
            console.error('Error fetching checklist history:', error);
            setError('Erro ao carregar histórico do checklist');
        } finally {
            setLoading(false);
        }
    };

    const handlePrintChecklist = async (serviceOrderId, historyId) => {
        if (!serviceOrderId) {
            console.error('Service order ID não disponível');
            return;
        }

        try {
            // Indica que está imprimindo este histórico
            setPrintingIds((prev) => new Set(prev).add(historyId));

            // Faz a requisição para baixar o PDF do checklist relacionado à ordem de serviço
            const response = await httpClient.get(
                `/checklist/service-order/pdf?checklistId=${checklistId}&orderServiceId=${serviceOrderId}`,
                { responseType: 'blob' }
            );

            if (response.status === 200 && response.data) {
                // Cria um Blob para o PDF
                const blob = response.data instanceof Blob
                    ? response.data
                    : new Blob([response.data], { type: 'application/pdf' });
                // Cria uma URL temporária para download
                const url = window.URL.createObjectURL(blob);
                // Cria um link para disparar o download
                const link = document.createElement('a');
                link.href = url;
                link.download = `Checklist_${checklistId}_OS_${serviceOrderId}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                throw new Error('Erro ao baixar PDF do checklist');
            }
        } catch (err) {
            console.error('Erro ao baixar PDF do checklist:', err);
            setError('Erro ao baixar PDF do checklist');
        } finally {
            setPrintingIds((prev) => {
                const newSet = new Set(prev);
                newSet.delete(historyId);
                return newSet;
            });
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            {/* header — ícone-badge + título + descrição + X */}
            <Box sx={{
                px: '22px',
                pt: '20px',
                pb: '14px',
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1.25,
            }}>
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        background: (t) => t.palette.gradient.brand,
                        mb: '10px',
                    }}>
                        <History size={18} strokeWidth={2} />
                    </Box>
                    <Typography sx={{ fontSize: '17px', fontWeight: 800, mb: '4px' }}>
                        Histórico de checklists
                    </Typography>
                    <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                        {historyData?.checklist_name || 'Carregando...'}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small" aria-label="Fechar">
                    <X size={15} strokeWidth={2} />
                </IconButton>
            </Box>

            {/* body — corpo rolável */}
            <Box sx={{ p: '18px 22px', maxHeight: '62vh', overflowY: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                        <CircularProgress size={30} />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, textAlign: 'center', py: 4 }}>
                        <Box sx={{ width: 56, height: 56, borderRadius: '50%', border: '2px dashed', borderColor: 'error.main', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'error.main' }}>
                            <AlertTriangle size={22} strokeWidth={2} />
                        </Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'error.main' }}>{error}</Typography>
                    </Box>
                ) : !historyData || !historyData.history || historyData.history.length === 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, textAlign: 'center', py: 4 }}>
                        <Box sx={{ width: 64, height: 64, borderRadius: '50%', border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
                            <History size={26} strokeWidth={2} />
                        </Box>
                        <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.secondary' }}>
                            Nenhum histórico encontrado
                        </Typography>
                        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                            Este checklist ainda não foi preenchido em nenhuma ordem de serviço
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                            {historyData.history.length} registro(s)
                        </Typography>
                        {historyData.history.map((item, index) => {
                            const historyKey = item.history_id || index;
                            const isPrinting = printingIds.has(historyKey);
                            return (
                                <Box
                                    key={historyKey}
                                    sx={{
                                        p: 2,
                                        borderRadius: '14px',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        position: 'relative',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.25 }}>
                                        <Typography sx={{ fontSize: '13.5px', fontWeight: 800, color: 'text.primary' }}>
                                            O.S. Nº {item.service_order_number}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {canSendWhatsapp && (
                                                <Tooltip title="Enviar checklist por WhatsApp">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setWhatsappTarget({ serviceOrderId: item.service_order_id, orderNumber: item.service_order_number })}
                                                            disabled={!item.service_order_id}
                                                        >
                                                            <Send size={15} strokeWidth={2} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Imprimir checklist preenchido">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handlePrintChecklist(item.service_order_id, historyKey)}
                                                        disabled={isPrinting}
                                                    >
                                                        {isPrinting ? <CircularProgress size={16} /> : <Printer size={15} strokeWidth={2} />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: 1.5, color: 'text.disabled' }}>
                                        <Calendar size={12} strokeWidth={2} />
                                        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                            {formatDate(item.completion_date)}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                                        <InfoBlock icon={User} label="Cliente" value={item.customer_name} />
                                        <InfoBlock icon={Car} label="Veículo" value={item.vehicle_name} />
                                        <InfoBlock icon={Wrench} label="Mecânico" value={item.mechanic_name} />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>

            {/* footer — ação à direita */}
            <Box sx={{
                p: '16px 22px 20px',
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'flex-end',
            }}>
                <Button variant="contained" onClick={onClose}>Fechar</Button>
            </Box>

            <ChecklistWhatsappDialog
                open={Boolean(whatsappTarget)}
                serviceOrderId={whatsappTarget?.serviceOrderId}
                orderNumber={whatsappTarget?.orderNumber}
                onClose={() => setWhatsappTarget(null)}
                onResult={(message, severity) => setFeedback({ message, severity })}
            />
            <Snackbar
                open={Boolean(feedback)}
                autoHideDuration={5000}
                onClose={() => setFeedback(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setFeedback(null)} severity={feedback?.severity || 'info'} variant="filled" sx={{ width: '100%' }}>
                    {feedback?.message}
                </Alert>
            </Snackbar>
        </Dialog>
    );
}
